import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {restrictToParentElement, restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {arrayMove, SortableContext, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {useMemo, useState} from "react";
import {GroupBase, MultiValue} from "react-select";

import {Button} from "@/components/button/Button.tsx";
import {ColumnOption} from "@/components/columnSelector/ColumnSelector.types.ts";
import {ListPanelSelect, ListPanelSelectOption} from "@/components/listPanelSelect/ListPanelSelect.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {SortableColumnItem} from "@/components/sortableColumnItem/SortableColumnItem.tsx";

import styles from "./ColumnSelector.module.scss";

interface ColumnSelectorProps {
  availableColumns: ColumnOption[];
  selectedColumns: ColumnOption[];
  onChange: (columns: ColumnOption[]) => void;
  ["aria-invalid"]?: boolean;
}

export const ColumnSelector = ({
  availableColumns,
  selectedColumns,
  onChange,
  ["aria-invalid"]: ariaInvalid,
}: ColumnSelectorProps) => {
  const [columnSearch, setColumnSearch] = useState("");

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));

  const columnsByCategory = useMemo(() => {
    const searchLower = columnSearch.toLowerCase();
    const groups: Record<string, ColumnOption[]> = {};
    for (const col of availableColumns) {
      if (searchLower && !col.label.toLowerCase().includes(searchLower)) continue;
      if (!groups[col.category]) {
        groups[col.category] = [];
      }
      groups[col.category].push(col);
    }
    return groups;
  }, [availableColumns, columnSearch]);

  const isSelected = (column: ColumnOption) => selectedColumns.some((col) => col.id === column.id);

  const allSelected = availableColumns.length > 0 && availableColumns.every(isSelected);

  const columnById = useMemo(() => {
    const map = new Map<string, ColumnOption>();
    for (const col of availableColumns) map.set(col.id, col);
    for (const col of selectedColumns) if (!map.has(col.id)) map.set(col.id, col);
    return map;
  }, [availableColumns, selectedColumns]);

  const groupedOptions = useMemo<GroupBase<ListPanelSelectOption<string>>[]>(
    () =>
      Object.entries(columnsByCategory).map(([category, columns]) => ({
        label: category,
        options: columns.map((col) => ({value: col.id, title: col.label})),
      })),
    [columnsByCategory]
  );

  const selectedOptions = useMemo<ListPanelSelectOption<string>[]>(
    () => selectedColumns.map((col) => ({value: col.id, title: col.label})),
    [selectedColumns]
  );

  const handleSelectChange = (newValue: MultiValue<ListPanelSelectOption<string>>) => {
    const next = newValue.map((opt) => columnById.get(opt.value)).filter((col): col is ColumnOption => Boolean(col));
    onChange(next);
  };

  const selectAll = () => {
    const missing = availableColumns.filter((col) => !isSelected(col));
    onChange([...selectedColumns, ...missing]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    if (!over || active.id === over.id) return;
    const oldIndex = selectedColumns.findIndex((col) => col.id === active.id);
    const newIndex = selectedColumns.findIndex((col) => col.id === over.id);
    onChange(arrayMove(selectedColumns, oldIndex, newIndex));
  };

  return (
    <div className={styles.columnsRow}>
      <div className={styles.columnPane}>
        <div className={styles.paneHeader}>
          <span className={"pulseTextBodySmallBold"}>Available Columns</span>
          <Button variant="linkBlue" size="fitContent" typography="linkBold" onClick={selectAll} disabled={allSelected}>
            Select All
          </Button>
        </div>
        <div className={styles.searchColumn} aria-invalid={ariaInvalid}>
          <SearchInput value={columnSearch} onChange={setColumnSearch} className={styles.searchInput} size={"xsmall"} />
          <div className={styles.listContainer} aria-invalid={ariaInvalid}>
            <ListPanelSelect
              className={styles.listPanel}
              options={groupedOptions}
              value={selectedOptions}
              onChange={handleSelectChange}
              showCheckbox
              hideTag
              hideSelectedOptions={false}
              noOptionsText="No columns found"
            />
          </div>
        </div>
      </div>

      <div className={styles.columnPane}>
        <div className={styles.paneHeader}>
          <span className={styles.label}>Column Order</span>
        </div>
        <div className={styles.orderList}>
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <SortableContext items={selectedColumns} strategy={verticalListSortingStrategy}>
              {selectedColumns.map((col) => (
                <SortableColumnItem key={col.id} col={col} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};
